import { ReactNode } from 'react';
import { TeContainer, TeContainerSlim } from '@topcon/tuicl-react';
import styled from 'styled-components';

import { Section } from '../../utils/styling';
import PageHeader, { PageHeaderProps } from './PageHeader';

interface PageLayoutProps extends PageHeaderProps {
  /** Page content, rendered in a growing region below the header. */
  children: ReactNode;
  /** Span the whole content area instead of capping at 1280px — for wide
   * server-paged tables (Customers / Invites / Requests, PRD D10). */
  fullWidth?: boolean;
  /** A `RightDrawer` (e.g. a filter drawer). Rendered as a direct child of the
   * layout so it spans the header and content top to bottom; nested inside
   * `children` it would start below the header and run past the viewport. */
  drawer?: ReactNode;
}

// Positioned so an absolutely placed `RightDrawer` anchors to the layout's
// top-right corner and takes its full height.
const LayoutSection = styled(Section)`
  position: relative;
`;

/**
 * Reusable page shell: a `PageHeader` above a growing content region, centered
 * on the app's tertiary background. TUICL-first successor to `ViewContainer` —
 * presentational only, the consumer owns data and search state.
 */
function PageLayout({ children, fullWidth = false, drawer, ...headerProps }: PageLayoutProps) {
  return (
    <TeContainer
      backgroundColor="background.tertiary"
      padding="xs"
      grow
      fill
      column
      gap={0}
      borderRadius={0}
      alignItems="center"
    >
      <LayoutSection
        $background="transparent"
        $height="100%"
        $gap="xs4"
        $alignment="stretch"
        $width={fullWidth ? 'full' : undefined}
      >
        <PageHeader {...headerProps} />
        {/* `fill` lets the panel shrink to the space left under the header
            instead of overflowing it, so tall content (e.g. a 100-row table)
            scrolls inside the panel rather than being clipped. */}
        <TeContainerSlim
          column
          fill
          width="100%"
          backgroundColor="background.primary"
          padding="m"
          borderRadius={4}
          overflow="hidden"
        >
          {children}
        </TeContainerSlim>
        {drawer}
      </LayoutSection>
    </TeContainer>
  );
}

export default PageLayout;
export type { PageLayoutProps };
